// ---------------------------------------------------------------------------
// Hook: useTrialStatus
// ---------------------------------------------------------------------------
// • Figures-out if the signed-in user still needs to start a trial.
// • Works for both accountants and clients, using PaymentService.
// • Adds `cancel` function to stop subscription.
// • Returns: { needsTrial, status, loading, error, cancel }
// ---------------------------------------------------------------------------

import { useEffect, useState, useCallback } from 'react';
import Constants from '../assests/Constants/constants';
import PaymentService from '../Services/payment.service';
import AuthService from '../Services/auth.service';

/**
 * @param {boolean} isAcc – true for accountant, false for client
 * @param {boolean} enabled – set to false to skip the call
 */
export const useTrialStatus = (isAcc, enabled = true) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);      // raw DTO
  const [needsTrial, setNeedsTrial] = useState(false); // convenience flag
  const [userId, setUserId] = useState(null);      // store resolved ID

  useEffect(() => {
    if (!enabled) return;

    let ignore = false;

    const fetchStatus = async () => {
      setLoading(true);
      setError(null);
      try {
        const id = await AuthService.getCurrentUserTypeId();
        if (ignore) return;

        setUserId(id);

        const dto = isAcc
          ? await PaymentService.accountant.status(id)
          : await PaymentService.client.status(id);

        if (ignore) return;

        setStatus(dto);
        setNeedsTrial(Boolean(dto?.needsTrial));
      } catch (ex) {
        if (!ignore) setError(ex.message || String(ex));
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    fetchStatus();
    return () => { ignore = true; };
  }, [isAcc, enabled]);

  const cancel = useCallback(async () => {
    if (!userId) return;

    return isAcc
      ? PaymentService.accountant.cancel(userId)
      : PaymentService.client.cancel(userId);
  }, [userId, isAcc]);

  return { needsTrial, status, loading, error, cancel };
};